import React, { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, RefreshCw, EyeOff, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { getSessionId } from "../lib/session";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Founder-only strip on Settings. Two tabs:
 *   • Visits — top 5 traffic sources by unique visitors (last-touch)
 *   • Signups — top 5 first-touch sources for `checkout_start` events (revenue attribution)
 * Renders nothing for regular users — the /api/analytics/is-owner probe gates
 * visibility so no data leaks even if someone inspects the DOM.
 */
export default function FounderAnalyticsStrip() {
  const [owner, setOwner] = useState(false);
  const [tab, setTab] = useState("visits");
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const sid = getSessionId();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/analytics/is-owner`, { params: { session_id: sid } });
        setOwner(!!r?.data?.owner);
      } catch { /* not owner */ }
    })();
  }, [sid]);

  const load = async (t = tab, d = days) => {
    setLoading(true);
    try {
      const url =
        t === "visits"
          ? `${BACKEND_URL}/api/analytics/top-referrers`
          : `${BACKEND_URL}/api/analytics/top-converting-referrers`;
      const r = await axios.get(url, { params: { session_id: sid, days: d, limit: 5 } });
      setData({ tab: t, ...r.data });
    } catch {
      setData({ tab: t, top: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (owner) load(tab, days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);

  if (!owner) return null;

  const top = data?.top || [];
  const isVisits = (data?.tab || "visits") === "visits";
  const total = isVisits ? data?.total_events : data?.total_conversions;
  const rowLabel = isVisits ? "visitors" : "conversions";
  const rowKey = isVisits ? "referrer" : "source";
  const countKey = isVisits ? "visitors" : "conversions";
  const maxCount = top.reduce((m, r) => Math.max(m, r[countKey] || 0), 1);

  const switchTab = (t) => {
    setTab(t);
    // Signups default to 30d, Visits default to 7d — signups are rarer.
    const nextDays = t === "signups" ? Math.max(days, 30) : days;
    setDays(nextDays);
    load(t, nextDays);
  };

  return (
    <>
    <Link
      to="/studio"
      data-testid="founder-studio-link"
      className="mt-6 flex items-center gap-3 rounded-2xl border border-[#BEFF38]/30 bg-[#BEFF38]/5 p-4 hover:border-[#BEFF38] transition-colors"
    >
      <Instagram className="h-5 w-5 text-[#BEFF38]" />
      <div className="flex-1">
        <div className="text-sm font-bold text-white">Instagram Content Studio</div>
        <div className="text-[11px] text-zinc-400">Plan, design and publish 4 posts a week</div>
      </div>
    </Link>
    <div
      data-testid="founder-analytics-strip"
      className="mt-6 rounded-2xl border border-[#ff7a1a]/30 bg-gradient-to-br from-[#ff7a1a]/10 to-transparent p-4 sm:p-5"
    >
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-[#ff7a1a] shrink-0" strokeWidth={2.5} />
        <p className="text-[11px] uppercase tracking-[0.16em] font-black text-[#ff7a1a] truncate">Founder analytics</p>
      </div>

      {/* Tabs */}
      <div className="mt-3 inline-flex rounded-lg bg-white/5 p-0.5 border border-white/5">
        {[
          { k: "visits", label: "Visits" },
          { k: "signups", label: "Signups" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => switchTab(t.k)}
            data-testid={`founder-analytics-tab-${t.k}`}
            className={`px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-colors ${
              tab === t.k ? "bg-[#BEFF38] text-black" : "text-zinc-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Range + refresh */}
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => { setDays(d); load(tab, d); }}
              data-testid={`founder-analytics-range-${d}`}
              className={`px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wider transition-colors ${
                days === d ? "bg-white/10 text-white" : "text-zinc-500 hover:text-white"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
        <button
          onClick={() => load(tab, days)}
          data-testid="founder-analytics-refresh"
          aria-label="Refresh"
          className="p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-white/5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <p className="mt-2 text-[11px] text-zinc-500">
        {(total || 0).toLocaleString()} {isVisits ? "events tracked" : "checkout starts"} · last {days} days ·{" "}
        {isVisits ? "session-deduped" : "first-touch attribution"}
      </p>

      <div className="mt-3 space-y-1.5">
        {top.length === 0 && !loading && (
          <p className="text-xs text-zinc-500 italic flex items-center gap-1.5">
            <EyeOff className="w-3 h-3" />
            {isVisits ? "No traffic recorded yet" : "No signups in this window yet"}
          </p>
        )}
        {top.map((row, i) => {
          const count = row[countKey] || 0;
          const label = row[rowKey] || "Unknown";
          const width = Math.max(4, Math.round((count / maxCount) * 100));
          return (
            <div key={label + i} data-testid={`founder-analytics-row-${i}`} className="flex items-center gap-3">
              <span className="w-4 text-[10px] font-black text-zinc-500 tabular-nums">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-xs font-semibold text-white truncate">{label}</p>
                  <p className="text-xs font-black text-[#BEFF38] tabular-nums">{count}</p>
                </div>
                <div className="mt-1 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#ff7a1a] to-[#BEFF38]"
                    style={{ width: `${width}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!isVisits && top.length > 0 && (
        <p className="mt-3 text-[10px] text-zinc-600 leading-snug">
          Attribution: first-touch (the channel that originally brought the user), based on referrer + UTM captured on first visit.
        </p>
      )}
    </div>
    </>
  );
}
